import React, { useState, useCallback, useEffect } from "react";
import { Stack, Box, Alert, Typography } from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";

import { DashboardHeader } from "../shared";
import LocationSelector from "./LocationSelector";
import DashboardSubheader from "./DashboardSubheader";
import InfoButton from "./InfoButton";
import { HELP_CONTENT } from "../../constants/clinicalHelpContent";
import PanelKpiOverview from "./PanelKpiOverview";
import PanelAlerts from "./PanelAlerts";
import PanelMyPatients from "./PanelMyPatients";
import PanelInsights from "./PanelInsights";
import PanelRecall from "./PanelRecall";
import PanelTasks from "./PanelTasks";
import TaskModal from "./TaskModal";

import useLocationSelector from "../../hooks/useLocationSelector";
import { getLocations as getClinicalLocations } from "../../services/clinicalDataService";
import useClinicalDashboard from "../../hooks/useClinicalDashboard";
import useTasks from "../../hooks/useTasks";
import useAuthUser from "../../../../hooks/useAuthUser";
import { getEmployees } from "../../../../utils/services/control/masterDataService";
import { TASK_CATEGORIES, TASK_PRIORITIES } from "../../constants/clinicalDashboard";

/**
 * ClinicalDashboard — Orquestador principal del dashboard clínico.
 * Renderiza Header + LocationSelector + 6 paneles + TaskModal.
 */
export default function ClinicalDashboard() {
  const {
    locations,
    selectedLocation,
    setSelectedLocation,
    loading: locationsLoading,
  } = useLocationSelector({
    getLocations: getClinicalLocations,
    storageKey: "mcm_selected_location",
    errorLabel: "clinical locations",
  });

  const {
    summary,
    alerts,
    patients,
    careGaps,
    coding,
    recall,
    loading,
    error,
  } = useClinicalDashboard(selectedLocation?.key);

  const {
    tasks,
    stats: taskStats,
    createTask,
    completeTask,
    updateTask,
    deleteTask,
  } = useTasks(selectedLocation?.locationId || null);

  const currentUser = useAuthUser();
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getEmployees({ status: 1 })
      .then((data) => { if (!cancelled) setEmployees(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ── Task Modal State ─────────────────────────────────────────
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskModalDefaults, setTaskModalDefaults] = useState(null);

  const openTaskModal = useCallback((defaults = null) => {
    setTaskModalDefaults(defaults);
    setTaskModalOpen(true);
  }, []);

  const handleCreateTask = useCallback(
    (taskData) => {
      const categoryId = TASK_CATEGORIES.find((c) => c.key === taskData.category)?.id || TASK_CATEGORIES[8]?.id;
      const priorityId = TASK_PRIORITIES.find((p) => p.key === taskData.priority)?.id || TASK_PRIORITIES[1]?.id;
      createTask({
        ...taskData,
        categoryId,
        priorityId,
        locationId: selectedLocation?.locationId || null,
        creatorId: currentUser?.employeeId || currentUser?.employee || null,
      });
    },
    [createTask, selectedLocation, currentUser]
  );

  // ── Panel CTAs ───────────────────────────────────────────────

  // Panel 2 (Alerts) → create follow-up task
  const handleAlertCreateTask = useCallback(
    (patient, type) => {
      const typeLabel = type === "admitted" ? "Admitted" : type === "discharged" ? "Discharged" : "Emergency";
      openTaskModal({
        title: `Follow-up ${typeLabel}: ${patient.name}`,
        category: "follow_up",
        priority: "high",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Panel 3 (Worklist) → assign task to patient
  const handlePatientCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `Task: ${patient.name}`,
        category: "clinical",
        priority: "medium",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Panel 4 (Insights) → create task from care gaps or recall
  const handleInsightsCreateTask = useCallback(
    (taskData) => {
      openTaskModal(taskData);
    },
    [openTaskModal]
  );

  // Panel 5 (Recall) → create task pre-filled
  const handleRecallCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `Patient Recall: ${patient.name}`,
        category: "patient_recall",
        priority: "medium",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Patients list for autocomplete
  const patientList = patients?.patients || patients || [];

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <DashboardHeader
        title="Clinical Dashboard"
        subtitle="Your command center for patient care, quality measures, and clinical insights"
        icon={<LocalHospitalIcon />}
        color="primary"
      />

      {/* Location Selector + Subheader */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <LocationSelector
            locations={locations}
            selectedLocation={selectedLocation}
            onChangeLocation={setSelectedLocation}
            loading={locationsLoading}
          />
          <InfoButton helpContent={HELP_CONTENT.locationSelector} />
        </Stack>
        <DashboardSubheader
          summary={summary}
          location={selectedLocation}
          loading={loading.summary}
        />
      </Stack>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ borderRadius: 3, "& .MuiAlert-icon": { alignItems: "center" } }}
        >
          <Typography fontWeight={600}>Something went wrong</Typography>
          <Typography variant="body2">{String(error)}</Typography>
        </Alert>
      )}

      {/* Panel 1: KPI Overview */}
      <PanelKpiOverview
        kpis={summary?.kpis}
        loading={loading.summary}
      />

      {/* Panel 2: Real-Time Alerts */}
      <PanelAlerts
        alerts={alerts}
        loading={loading.alerts}
        onCreateTask={handleAlertCreateTask}
      />

      {/* Row: Panel 3 (65%) + Panel 4 (35%) */}
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", lg: "1.85fr 1fr" },
          alignItems: "start",
        }}
      >
        <PanelMyPatients
          patients={patients}
          pcps={summary?.pcps}
          loading={loading.patients}
          onCreateTask={handlePatientCreateTask}
        />
        <PanelInsights
          careGaps={careGaps}
          coding={coding}
          recall={recall}
          loading={loading}
          onCreateTask={handleInsightsCreateTask}
        />
      </Box>

      {/* Row: Panel 5 (65%) + Panel 6 (35%) */}
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", lg: "1.85fr 1fr" },
          alignItems: "start",
        }}
      >
        <PanelRecall
          recall={recall}
          loading={loading.recall}
          onCreateTask={handleRecallCreateTask}
        />
        <PanelTasks
          tasks={tasks}
          stats={taskStats}
          loading={loading.summary}
          onComplete={(id) => completeTask(id, currentUser?.employeeId || currentUser?.employee)}
          onCancel={(id) => updateTask(id, 29, currentUser?.employeeId || currentUser?.employee)}
          onDelete={(id) => deleteTask(id, currentUser?.employeeId || currentUser?.employee)}
          onEdit={(task) => openTaskModal(task)}
          onNewTask={() => openTaskModal()}
        />
      </Box>

      {/* Task Modal */}
      <TaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onSubmit={handleCreateTask}
        defaultValues={taskModalDefaults}
        patients={patientList}
        employees={employees}
        currentUser={currentUser}
      />
    </Stack>
  );
}
